import { Autocomplete, FilterOptionsState } from '@mui/material'
import { SubmitEvent, SyntheticEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { IAgent } from '../../lib/interface/agent'
import { affichageNomPrenom, champSansAccent, getIdNoeudsAscendants } from '../Utils/fonctions'
import { useFetchContext } from '../../hook/UseFetchContext'
import { SET_NOEUD_OUVERT, SET_NOEUD_SELECTED } from '../../lib/typeKey'
import { renderInputAccessibleGenerator } from '../commun/renderInputAccessibleGenerator'
import useHandleChangeAgent from '../../hook/UseHandleChangeAgent'
import { getAgentByRecherche } from '../Utils/searchUtils'

export default function RechercheAgent() {
  const { state, dispatch } = useFetchContext()
  const agentsTotal = state.agentTotalState.agentsTotal
  const noeuds = state.dimensionState.noeuds
  const [inputValue, setInputValue] = useState<string | null>(null)
  const { idep } = useParams()
  const handleChangeAgent = useHandleChangeAgent()

  const selectAgent = (agent: IAgent | null | undefined) => {
    let noeud: (INoeudDimension | null)[] = [null]
    handleChangeAgent(agent)
    if (agent?.affectation) {
      noeud = noeuds.filter((n: INoeudDimension) => n.valeurs.includes(agent.affectation.unite))
    }
    dispatch({ type: SET_NOEUD_OUVERT, payload: getIdNoeudsAscendants(noeuds, noeud[0]) })
    dispatch({ type: SET_NOEUD_SELECTED, payload: noeud[0]?.identifiantNoeudDimension ?? null })
  }

  useEffect(() => {
    if (idep) {
      const agent = agentsTotal.find(
        (value: IAgent) => value.identifiantsAgent.idep?.toUpperCase() === idep?.toUpperCase()
      )
      selectAgent(agent)
    }
  }, [agentsTotal, idep, selectAgent])

  // création d'une map avec le code des unités et le timbre que l'on récupère dans le libellé
  const mapCodeLibelle: { [key: string]: string } = {}

  noeuds.forEach((noeud: INoeudDimension) => {
    noeud.valeurs.forEach((valeur: string) => {
      const unite = noeud.libelle.split(' ~ ')[1]
      if (unite) {
        mapCodeLibelle[valeur] = ` (${unite})`
      }
    })
  })

  const searchAgent = (recherche: string) => {
    const agentsPartiel = agentsTotal.filter((agent: IAgent) => agent.etatCivil.nomUsage !== null)
    const result = getAgentByRecherche(agentsPartiel, champSansAccent(recherche))
    return result
  }

  const miseEnFormeAgentName = (agent: IAgent) => {
    if (!agent.affectation?.unite) {
      return ''
    }
    if (mapCodeLibelle[agent.affectation.unite]) {
      return (
        affichageNomPrenom(agent.etatCivil.nomUsage, agent.etatCivil.prenomUsuel) +
        mapCodeLibelle[agent.affectation.unite]
      )
    } else {
      return affichageNomPrenom(agent.etatCivil.nomUsage, agent.etatCivil.prenomUsuel)
    }
  }

  const renderAgentName = (agent: IAgent | string) => {
    if (typeof agent === 'string') return agent
    return miseEnFormeAgentName(agent)
  }

  const label = 'Recherche par nom/prénom, tél, bureau, idep, balf, attributions'

  const filterOptions = (_: IAgent[], state: FilterOptionsState<IAgent>) => {
    return searchAgent(state.inputValue)
  }

  const onChange = (_: SyntheticEvent<Element, Event> | null, value: string | IAgent | null) => {
    if (typeof value === 'string') {
      setInputValue(champSansAccent(value))
      return
    }
    selectAgent(value)
    setInputValue(value?.identifiantsAgent.idep ?? null)
  }

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault()
    const firstOption = inputValue ? (searchAgent(inputValue)?.[0] ?? null) : null
    onChange(null, firstOption)
  }

  const agent = inputValue && getAgentByRecherche(agentsTotal, inputValue.toLocaleLowerCase())?.[0]

  return (
    <form onSubmit={onSubmit}>
      <Autocomplete
        clearText={'Effacer la saisie'}
        clearOnEscape
        options={agentsTotal}
        getOptionLabel={renderAgentName}
        filterOptions={filterOptions}
        freeSolo
        size="small"
        aria-label={label}
        onChange={onChange}
        value={agent || null}
        renderInput={renderInputAccessibleGenerator(label, 'recherche-agents')}
      />
    </form>
  )
}
